<script setup lang="ts">
import type { Schemas } from '#shopware'
import MiscCarousel from '~/components/misc/MiscCarousel.vue'

const props = defineProps<{
    content: Schemas['CmsElementHeroSlider']
}>()

const AUTOPLAY_INTERVAL_MS = 7000

const sortedSlides = computed(() => {
    return [...Object.values(props.content.data?.elements ?? {})].sort((a, b) => a.position - b.position)
})

const firstSlide = computed(() => sortedSlides.value[0])

const visibleSlides = computed(() => {
    return sortedSlides.value.filter((slide) => slide.image != null && !slide.isHidden)
})

// MiscCarousel only needs something to index/key its slides by - the real slide data
// is looked up from `visibleSlides` inside the #default slot.
const carouselSlides = computed(() => visibleSlides.value.map((_, index) => ({ id: index })))

function hasContent(input: string | null | undefined): input is string {
    return input != null && input.trim() !== ''
}

const carouselRef = ref<InstanceType<typeof MiscCarousel> | null>(null)
const slideRefs = ref<(HTMLElement | null)[]>([])
const currentSlideIndex = ref(0)
const isPlaying = ref(false)

// Explicit user choice via the play/pause button - takes priority over the
// temporary hover/focus/touch pause below, e.g. it survives mouseleave.
const isManuallyPaused = ref(false)

const dotsContainerClass = computed(() => {
    const base = 'hero-slider__navdots absolute bottom-0 left-0 right-0 mx-auto mb-8 h-10 w-60 sm:w-72 gap-3 flex justify-between items-end'
    return isPlaying.value ? base : `${base} is-paused`
})

let autoplayTimer: ReturnType<typeof setInterval> | null = null

function prefersReducedMotion() {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const sliderRoot = ref<HTMLElement | null>(null)

// Autoplay drives goTo() and keeps the 7s dot progress animation alive, so it must not
// run off-screen or in a background tab. threshold 0.25 because the slider is 650-1029px
// tall and would otherwise count as visible when only its nav dots peek in.
const mayAutoplay = useMayAnimate(sliderRoot, { threshold: 0.25 })

function play() {
    if (visibleSlides.value.length <= 1 || prefersReducedMotion()) return
    if (!mayAutoplay.value) return

    if (autoplayTimer) clearInterval(autoplayTimer)
    isPlaying.value = true
    autoplayTimer = setInterval(() => {
        carouselRef.value?.nextSlide()
    }, AUTOPLAY_INTERVAL_MS)
}

function pause() {
    if (autoplayTimer) {
        clearInterval(autoplayTimer)
        autoplayTimer = null
    }
    isPlaying.value = false
}

// Hover/focus/touch only pause temporarily - they resume on leave unless the
// user explicitly paused via the button.
function pauseTemporarily() {
    pause()
}

function resumeIfNotManuallyPaused() {
    if (!isManuallyPaused.value) play()
}

watch(mayAutoplay, (may) => {
    if (may) resumeIfNotManuallyPaused()
    else pause()
})

function togglePlayPause() {
    isManuallyPaused.value = !isManuallyPaused.value
    if (isManuallyPaused.value) pause()
    else play()
}

function prevSlide() {
    carouselRef.value?.previousSlide()
}

function nextSlide() {
    carouselRef.value?.nextSlide()
}

function updateRovingTabindex(activeIndex: number) {
    slideRefs.value.forEach((slideEl, index) => {
        if (slideEl == null) return
        const focusableElements = slideEl.querySelectorAll<HTMLElement>(
            'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        focusableElements.forEach((element) => {
            element.setAttribute('tabindex', index === activeIndex ? '0' : '-1')
        })
    })
}

function onSlideChange(index: number) {
    currentSlideIndex.value = index
    updateRovingTabindex(index)
}

onMounted(() => {
    nextTick(() => {
        updateRovingTabindex(currentSlideIndex.value)
        play()
    })
})

onBeforeUnmount(() => {
    if (autoplayTimer) clearInterval(autoplayTimer)
})
</script>

<template>
    <template v-if="sortedSlides.length === 1 && firstSlide != null">
        <MiscLink
            v-if="hasContent(firstSlide.buttonText) && hasContent(firstSlide.buttonTarget)"
            :to="firstSlide.buttonTarget"
            class="hero-slider -mx-2 md:-mx-0 mb-24"
        >
            <template v-if="firstSlide.version === '1'">
                <StructureElementHeroSliderSlide :slide="firstSlide" :index="0" />
            </template>
            <template v-else>
                <StructureElementHeroSliderSlideSimple :slide="firstSlide" :index="0" />
            </template>
        </MiscLink>
        <div v-else class="cursor-default">
            <template v-if="firstSlide.version === '1'">
                <StructureElementHeroSliderSlide :slide="firstSlide" :index="0" />
            </template>
            <template v-else>
                <StructureElementHeroSliderSlideSimple :slide="firstSlide" :index="0" />
            </template>
        </div>
    </template>

    <template v-else-if="sortedSlides.length > 1">
        <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events vuejs-accessibility/no-static-element-interactions -->
        <div
            ref="sliderRoot"
            class="hero-slider relative -mx-2 md:-mx-0"
            @mouseenter="pauseTemporarily"
            @mouseleave="resumeIfNotManuallyPaused"
            @focusin="pauseTemporarily"
            @focusout="resumeIfNotManuallyPaused"
            @touchstart="pauseTemporarily"
            @keydown.right="nextSlide"
            @keydown.left="prevSlide"
        >
            <MiscCarousel
                ref="carouselRef"
                :slides="carouselSlides"
                :label="$t('structure.slot.heroSlider.main')"
                :enable-autoplay="false"
                :enable-infinite-scroll="true"
                :enable-arrows="false"
                :enable-dots="true"
                :dots-container-class="dotsContainerClass"
                :gap="0"
                carousel-width="100%"
                @slide-change="onSlideChange"
            >
                <template #default="{ index }">
                    <div
                        v-if="visibleSlides[index] != null"
                        :ref="el => { slideRefs[index] = el as HTMLElement | null }"
                        class="h-[650px] sm:h-[900px] md:h-[1029px] lg:h-[684px] w-full aspect-[2/3] md:aspect-[12/5] relative"
                    >
                        <MiscLink
                            v-if="hasContent(visibleSlides[index]!.buttonText) && hasContent(visibleSlides[index]!.buttonTarget)"
                            :to="visibleSlides[index]!.buttonTarget"
                            class="hero-slider mx-0 mb-24 block"
                        >
                            <template v-if="visibleSlides[index]!.version === '1'">
                                <StructureElementHeroSliderSlide :slide="visibleSlides[index]!" :index="index" />
                            </template>
                            <template v-else>
                                <StructureElementHeroSliderSlideSimple :slide="visibleSlides[index]!" :index="index" />
                            </template>
                        </MiscLink>
                        <div v-else class="cursor-default">
                            <template v-if="visibleSlides[index]!.version === '1'">
                                <StructureElementHeroSliderSlide :slide="visibleSlides[index]!" :index="index" />
                            </template>
                            <template v-else>
                                <StructureElementHeroSliderSlideSimple :slide="visibleSlides[index]!" :index="index" />
                            </template>
                        </div>
                    </div>
                </template>
            </MiscCarousel>

            <button
                type="button"
                class="z-30 hidden md:flex absolute bottom-0 left-12 mb-9 sm:mb-8 btn btn-sm bg-secondary w-14 h-11 text-secondary-content no-animation"
                :aria-label="isManuallyPaused ? $t('structure.slot.heroSlider.play') : $t('structure.slot.heroSlider.pause')"
                @click="togglePlayPause"
            >
                <MiscIcon :name="isManuallyPaused ? 'play' : 'pause'" size="sm" />
            </button>

            <div
                role="group"
                :aria-label="$t('structure.slot.heroSlider.controlsLabel')"
                class="hidden md:block absolute bottom-0 right-3 mb-8 mr-12"
            >
                <button
                    type="button"
                    class="swiper-custom-button-prev relative m-auto z-30 btn bg-secondary rounded-l-full border-none"
                    :aria-label="$t('structure.slot.heroSlider.prev')"
                    @click="prevSlide"
                >
                    <MiscIcon name="arrow-left" />
                </button>
                <button
                    type="button"
                    class="swiper-custom-button-next relative m-auto z-30 btn bg-secondary rounded-r-full border-none"
                    :aria-label="$t('structure.slot.heroSlider.next')"
                    @click="nextSlide"
                >
                    <MiscIcon name="arrow-right" />
                </button>
            </div>
        </div>
    </template>
</template>
